/**
 * The landing page's locale. Its strings live in the app tables (app.ts); this
 * module only decides which one a visitor sees before they have an account.
 */
import { INTERFACE_TABLES, type AppLocale, type AppTranslations } from './app'
import { browserLanguages, matchBrowserLanguage, rememberedReleasedCodes, selectableInterfaceLanguages, type RegistryLanguage } from './registry'

export type LandingLocale = AppLocale
export type LandingT = AppTranslations

export const LANDING_LOCALE_KEY = 'landing-locale'

function savedLandingLocale(): string | null {
  try {
    return localStorage.getItem(LANDING_LOCALE_KEY)
  } catch {
    return null
  }
}

/**
 * The visitor's saved choice, else the first of the browser's preferred
 * languages that is offered (an interface table, and released — ADR-111 D5); else English.
 */
export function detectLocale(registry: RegistryLanguage[] | null): LandingLocale {
  const offered = selectableInterfaceLanguages(registry).map((l) => l.code)
  const saved = savedLandingLocale()
  if (saved && offered.includes(saved)) return saved
  return matchBrowserLanguage(browserLanguages(), offered)
}

/**
 * The same choice made before the registry has loaded: what main.tsx starts
 * loading while the registry is still in flight. Only a guess (detectLocale
 * decides, once release is known), so it is limited to the languages this
 * browser last saw released: an unreleased table is never downloaded on a
 * guess. With no such memory (a first visit) the guess is English, and the
 * table waits for the registry.
 */
export function guessLocale(): LandingLocale {
  const released = rememberedReleasedCodes() ?? ['en']
  const offered = Object.keys(INTERFACE_TABLES).filter((code) => released.includes(code))
  const saved = savedLandingLocale()
  if (saved && offered.includes(saved)) return saved
  return matchBrowserLanguage(browserLanguages(), offered)
}
